import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, Linkedin, Twitter, Facebook } from "lucide-react";
import Logo from "./Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="bg-ink-900 text-white">
      <div className="container-page py-16">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center" aria-label="SnapPay home">
              <span className="inline-flex items-center rounded-lg bg-white px-3 py-2">
                <Logo className="h-8" />
              </span>
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
              Pioneering secure payment solutions for high-risk merchants. Building the global operating system for fintech, starting with payments.
            </p>
            <form onSubmit={subscribe} className="mt-6">
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/50">
                Stay in the loop
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-brand-400 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="rounded-full bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                >
                  {status === "loading" ? "Sending..." : "Subscribe"}
                </button>
              </div>
              {status === "success" && (
                <p className="mt-2 text-xs text-brand-300">Thanks — you are subscribed.</p>
              )}
              {status === "error" && (
                <p className="mt-2 text-xs text-red-300">Something went wrong. Try again.</p>
              )}
            </form>
          </div>

          <FooterCol title="Company">
            <FooterLink to="/">Home</FooterLink>
            <FooterLink to="/about-us">About Us</FooterLink>
            <FooterLink to="/#solutions">Solutions</FooterLink>
            <FooterLink to="/#blog">Blog</FooterLink>
            <FooterLink to="/contact-us">Contact</FooterLink>
          </FooterCol>

          <FooterCol title="Solutions">
            <FooterLink to="/#solutions">Merchant Accounts</FooterLink>
            <FooterLink to="/#solutions">Alternative Payment Methods</FooterLink>
            <FooterLink to="/#solutions">Chargeback Mitigation</FooterLink>
            <FooterLink to="/#solutions">Payment Compliance</FooterLink>
            <FooterLink to="/#solutions">Payouts</FooterLink>
          </FooterCol>

          <FooterCol title="Contact">
            <li className="flex items-start gap-2 text-sm text-white/60">
              <Mail className="mt-0.5 h-4 w-4 text-brand-300" />
              <a href="mailto:hello@snappay.co" className="hover:text-white">hello@snappay.co</a>
            </li>
          </FooterCol>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-white/50">
            Copyright ©2026 SnapPay.co. All Rights Reserved.
          </p>
          <div className="flex items-center gap-3">
            <SocialIcon href="#" label="LinkedIn"><Linkedin className="h-4 w-4" /></SocialIcon>
            <SocialIcon href="#" label="Twitter"><Twitter className="h-4 w-4" /></SocialIcon>
            <SocialIcon href="#" label="Facebook"><Facebook className="h-4 w-4" /></SocialIcon>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
        {title}
      </h4>
      <ul className="mt-4 space-y-3">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="text-sm text-white/70 transition hover:text-white">
        {children}
      </Link>
    </li>
  );
}

function SocialIcon({ href, label, children }) {
  return (
    <a
      href={href}
      aria-label={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:border-brand-400 hover:bg-brand-500/10 hover:text-white"
    >
      {children}
    </a>
  );
}
